import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Megaphone, Handshake, MapPin } from "lucide-react";
import { IMG, scrollToId } from "../config";

const LINES = ["Hadir, Mendengar,", "dan Bergerak", "untuk Cianjay."];

const Seal = () => (
  <div className="absolute -bottom-8 -left-8 sm:-left-12 w-28 h-28 sm:w-36 sm:h-36 animate-spin-slow" data-testid="hero-seal">
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <defs>
        <path id="circlePath" d="M 60,60 m -44,0 a 44,44 0 1,1 88,0 a 44,44 0 1,1 -88,0" />
      </defs>
      <circle cx="60" cy="60" r="58" className="fill-terra" />
      <circle cx="60" cy="60" r="30" fill="none" stroke="#F7F4EE" strokeWidth="1" strokeDasharray="3 4" />
      <text fill="#F7F4EE" fontSize="11.5" fontFamily="JetBrains Mono, monospace" letterSpacing="2.5">
        <textPath href="#circlePath">PELAYAN WARGA • CIANJAY • SIAGA 24 JAM •</textPath>
      </text>
      <text x="60" y="66" textAnchor="middle" fill="#F7F4EE" fontSize="17" fontFamily="Playfair Display, serif" fontWeight="700">
        M.H.
      </text>
    </svg>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} id="hero" data-testid="hero-section" className="relative overflow-hidden pt-32 sm:pt-40 pb-20 sm:pb-28">
      <div className="pointer-events-none absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-gold/20 blur-3xl" />
      <div className="pointer-events-none absolute top-64 -left-40 w-[26rem] h-[26rem] rounded-full bg-terra/10 blur-3xl" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2 mb-8"
            data-testid="hero-location-badge"
          >
            <MapPin size={14} className="text-terra" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-soft">
              Desa Cianjay · Kecamatan Cianjir
            </span>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-serif font-bold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">
            {LINES.map((line, i) => (
              <span key={i} className="mask-line pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {i === 2 ? (
                    <>
                      untuk <em className="not-italic text-terra">Cianjay.</em>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-soft"
          >
            Saya <span className="font-semibold text-ink">Mulyono M.H.</span>, Kades yang percaya bahwa
            kesejahteraan warga tidak dibangun dari meja kerja, melainkan dari sapaan hangat di pos ronda
            dan aksi nyata di lapangan.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-aspirasi"
              onClick={() => scrollToId("aspirasi")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-forest px-7 py-4 text-sm sm:text-base font-semibold text-bg transition-all hover:bg-foresthover hover:-translate-y-1 shadow-[0_14px_35px_rgba(30,58,43,0.28)]"
            >
              <Megaphone size={18} className="transition-transform group-hover:rotate-[-12deg]" />
              Sampaikan Aspirasimu
            </button>
            <button
              data-testid="hero-cta-kenal"
              onClick={() => scrollToId("profil")}
              className="inline-flex items-center gap-2.5 rounded-full border-2 border-forest/25 px-7 py-[14px] text-sm sm:text-base font-semibold text-forest transition-all hover:border-forest hover:-translate-y-1"
            >
              <Handshake size={18} />
              Kenal Lebih Dekat
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-12 flex flex-wrap gap-3"
            data-testid="hero-stats"
          >
            {["3 Dusun Terjangkau", "24/7 Siaga Warga", "100% Pintu Terbuka"].map((s) => (
              <span key={s} className="rounded-full bg-badge border border-line px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-ink/75">
                {s}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImg }} className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto w-[82%] sm:w-[70%] lg:w-full max-w-md"
          >
            <div className="absolute inset-0 translate-x-4 translate-y-4 arch-frame bg-gold/50" />
            <div className="relative arch-frame overflow-hidden border-4 border-surface shadow-[0_30px_60px_rgba(30,58,43,0.25)]">
              <img
                src={IMG.potret}
                alt="Mulyono M.H., Kepala Desa Cianjay"
                data-testid="hero-portrait"
                className="w-full aspect-[4/5] object-cover object-top"
              />
            </div>
            <Seal />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
